import React, { useEffect, useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { CCEuro24GameId, DEFAULT_MAINTENANCE_TIME, T } from '../../../constants';
import { MaintenanceCountdownDigits } from './maintenance-countdown-digits';
import { useStore, useTranslation } from '../../../hooks';

import PascalLogo from '../../../assets/media/logos/pascal-logo-not-found.png';
import TronixLogo from '../../../assets/media/logos/tronix.png';

import './style.scss';


interface MaintenanceProps {
}

const Maintenance: React.FC<MaintenanceProps> = observer(() => {
  const [time, setTime] = useState({ ...DEFAULT_MAINTENANCE_TIME });
  const { gameStore, maintenanceStore } = useStore();

  const { launchData: { data: launchData }, isTronix } = gameStore;
  // Pushed over the socket mid-session: the end comes with the push, launch data has none.
  const { showMaintenancePage: isPushed, notice, endTime } = maintenanceStore;
  const endDate = isPushed ? notice?.endDate : launchData?.endDate;
  const t = useTranslation();

  const showPascalLogo = launchData?.gameId !== CCEuro24GameId;

  const calculateTime = useCallback(() => {
    if (!endDate) return { ...DEFAULT_MAINTENANCE_TIME };
    const now = new Date().getTime();
    const duration = new Date(endDate).getTime() - now;
    if (duration <= 0) return { hours: 0, minutes: 0, seconds: 0 };

    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((duration % (1000 * 60)) / 1000);

    return { hours, minutes, seconds };
  }, [endDate]);

  useEffect(() => {
    setTime(calculateTime());
    const interval = setInterval(() => setTime(calculateTime()), 1000);

    const reloadDelay = isPushed ? Math.max(0, endTime - Date.now()) : launchData?.timeoutMilliseconds;
    const reloadTimeout = setTimeout(() => gameStore.reloadGame(), reloadDelay);

    return () => {
      clearInterval(interval);
      clearTimeout(reloadTimeout);
    };
  }, []);

  return (
    <div className={`maintenance-wrapper ${isTronix ? "tronix" : ""}`}>
      <div className="maintenance-countdown-wrapper">
        <div className="maintenance-logo">
          {launchData?.gameLogoUrl && <img src={launchData?.gameLogoUrl} alt="game-logo" />}
        </div>
        <div className="maintenance-countdown-block">
          <p className="maintenance-countdown-text">{t("UNDER_MAINTENANCE_DESC")}</p>
        </div>
      </div>

      <div className="maintenance-countdown">
        <div className="clock">
          <div className="clock-content">
            <MaintenanceCountdownDigits name={t(T.HOURS)} digits={time.hours} />
            <span className="separator">:</span>
            <MaintenanceCountdownDigits name={t(T.MINUTES)} digits={time.minutes} />
            <span className="separator">:</span>
            <MaintenanceCountdownDigits name={t(T.SECONDS)} digits={time.seconds} />
          </div>
        </div>
      </div>
      {
        showPascalLogo && (
          <div className="pascal-logo">
            <img
              alt="pascal-logo"
              src={isTronix ? TronixLogo : PascalLogo}
            />
          </div>
        )
      }
    </div>
  );
});

export default Maintenance;
